<header class="redesign-page-header">
  <div class="redesign-title-block">
    <i class="fa-solid fa-microchip redesign-title-icon" aria-hidden="true"></i>
    <h1 class="redesign-page-title">Operational Insights</h1>
  </div>
  <div class="fleet-select" aria-label="Fleet selection">
    <app-dropdown
      [control]="fleetControl"
      [options]="fleetOptions"
      [label]="''"
      placeholder="Select fleet"
      dataId="operational-insights-fleet"
    ></app-dropdown>
  </div>
</header>

<section class="stat-cards" aria-label="Device status summary">
  @for (card of statCards; track card.key) {
  <article
    class="stat-card stat-card--clickable"
    [ngClass]="['stat-card--' + card.variant, isCardSelected(card) ? 'stat-card--selected' : '']"
    role="button"
    tabindex="0"
    [attr.aria-pressed]="isCardSelected(card)"
    [attr.aria-label]="card.label + ', ' + (card.value | number) + ' devices'"
    (click)="selectStatCard(card, $event)"
    (keydown)="onStatCardKeydown(card, $event)"
  >
    <img [src]="asset(card.icon)" alt="" class="stat-card__icon" width="48" height="48" />
    <p class="stat-card__value">{{ card.value | number }}</p>
    <p class="stat-card__label">{{ card.label }}</p>
  </article>
  }
</section>

<section class="charts-card" aria-labelledby="operational-insights-section-title">
  <div class="charts-card__head">
    <h2 id="operational-insights-section-title" class="charts-card__title">{{ chartsSectionTitle }}</h2>
    <div
      class="charts-card__calendar calendar d-inline-block"
      [hidden]="!showDateRangePicker"
      (clickOutside)="hideCalendar = !hideCalendar"
    >
      <app-date-time-calendar
        [clickOutside]="hideCalendar"
        [selectPreset]="'30days'"
        [disableToday]="true"
        [disableNav]="true"
        [timeSelection]="false"
        [hideLinks]="[
          'today',
          '15mins',
          '30mins',
          '1hour',
          '4hours',
          '12hours',
          '24hours',
          'monthtodate',
          'weektodate',
          'todaysofar'
        ]"
        (timeChangeEvent)="onTimeChange($event)"
      >
      </app-date-time-calendar>
    </div>
  </div>

  <div class="charts-card__body" [class.charts-card__body--hidden]="!showPieAndTrend">
    <div class="charts-col charts-col--pie">
      <div class="charts-card__chart-title">
        <span class="charts-card__chart-title-inner">
          {{ pieChartTitle }}
          <i class="fa-solid fa-circle-info info-icon" aria-hidden="true"></i>
        </span>
      </div>
      <div class="charts-pie-row">
        <div class="charts-pie-wrap charts-pie-wrap--chart-host">
          @if (selectedStat === 'notCommunicating' && isLoadingNotCommPie) {
          <div class="charts-pie__loading charts-pie__loading--overlay" aria-busy="true">
            <img src="assets/img/darby_loader_small.gif" alt="" />
          </div>
          }
          <canvas id="operationalInsightsPieChart" role="img" [attr.aria-label]="pieChartTitle"></canvas>
        </div>
        <div class="charts-legend" role="list">
          @for (leg of pieLegendItems; track leg.label) {
          <div class="legend-row" role="listitem">
            <span class="legend-dot" [style.background-color]="leg.color"></span>
            <span>{{ leg.label }}</span>
          </div>
          }
        </div>
      </div>
    </div>
    <div class="charts-divider" aria-hidden="true"></div>
    <div class="charts-col charts-col--trend">
      <div class="charts-card__chart-title">
        <span class="charts-card__chart-title-inner">{{ trendChartTitle }}</span>
      </div>
      <div class="charts-trend">
        <canvas
          id="operationalInsightsLineChart"
          role="img"
          [attr.aria-label]="trendChartTitle"
          [style.display]="isLoadingLineGraph ? 'none' : 'block'"
        ></canvas>
        @if (isLoadingLineGraph) {
        <div class="charts-trend__loading">
          <img src="assets/img/darby_loader_small.gif" alt="" />
        </div>
        }
      </div>
    </div>
  </div>

  @if (selectedStat === 'communicating') {
  <div class="charts-card__tabs table-tabs" role="tablist">
    <button
      type="button"
      class="table-tab"
      [class.table-tab--active]="activeDeviceTab === 'healthy'"
      role="tab"
      [attr.aria-selected]="activeDeviceTab === 'healthy'"
      (click)="activeDeviceTab = 'healthy'"
    >
      Healthy Devices
    </button>
    <button
      type="button"
      class="table-tab"
      [class.table-tab--active]="activeDeviceTab === 'attention'"
      role="tab"
      [attr.aria-selected]="activeDeviceTab === 'attention'"
      (click)="activeDeviceTab = 'attention'"
    >
      Devices Requiring Attention
    </button>
  </div>
  }

  @if (selectedStat === 'notCommunicating') {
  <div class="charts-card__tabs table-tabs table-tabs--wrap" role="tablist">
    @for (bucket of notCommTabOrder; track bucket) {
    <button
      type="button"
      class="table-tab table-tab--compact"
      [class.table-tab--active]="notCommActiveBucket === bucket"
      role="tab"
      [attr.aria-selected]="notCommActiveBucket === bucket"
      (click)="setNotCommTab(bucket)"
    >
      {{ notCommBucketMeta[bucket].tabLabel }}
    </button>
    }
  </div>
  }

  @if (selectedStat === 'disconnected') {
  <div class="charts-card__tabs table-tabs" role="tablist">
    <div class="table-tab table-tab--active table-tab--static" role="tab" aria-selected="true">Devices</div>
  </div>
  }

  <div class="table-card" [attr.aria-label]="chartsSectionTitle + ' device list'">
    <div class="table-card__toolbar">
      <div class="search-field">
        <i class="fa-solid fa-magnifying-glass redesign-sidebar__search-icon" aria-hidden="true"></i>
        <input type="search" placeholder="Search Devices" aria-label="Search devices" />
      </div>
      <button
        type="button"
        class="btn-download"
        aria-label="Download device data as CSV"
        [disabled]="isDownloadingCsv"
        (click)="downloadDeviceData()"
      >
        <i class="fa-solid fa-cloud-arrow-down" style="color: #4b5563" aria-hidden="true"></i>
      </button>
    </div>

    <div class="table-wrap operational-insights-table-wrap">
      <ngx-datatable
        #operationalDeviceTable
        class="bootstrap alertTable reportsTable wrapcell move-customer-table active-table span-lastchild-fix added-device-datatable"
        [rows]="deviceRows"
        [columnMode]="ColumnMode.force"
        [headerHeight]="45"
        [footerHeight]="deviceRows.length ? 45 : 0"
        [rowHeight]="'auto'"
        [scrollbarH]="true"
        [loadingIndicator]="isLoadingDevices"
        [limit]="10"
        [reorderable]="false"
        [messages]="{ emptyMessage: 'No devices to display' }"
      >
        <ngx-datatable-column
          [resizeable]="false"
          [sortable]="false"
          [draggable]="false"
          [width]="56"
          [canAutoResize]="false"
          name="#"
        >
          <ng-template let-rowIndex="rowIndex" ngx-datatable-cell-template>
            {{ rowIndex + 1 }}
          </ng-template>
        </ngx-datatable-column>
        <ngx-datatable-column
          [resizeable]="false"
          [sortable]="true"
          prop="serialNumber"
          name="Serial Number"
          [minWidth]="140"
        >
          <ng-template let-row="row" ngx-datatable-cell-template>
            {{ row.serialNumber }}
          </ng-template>
        </ngx-datatable-column>
        <ngx-datatable-column [resizeable]="false" [sortable]="true" prop="iccid" name="ICCID" [minWidth]="160">
          <ng-template let-row="row" ngx-datatable-cell-template>
            {{ row.iccid }}
          </ng-template>
        </ngx-datatable-column>
        <ngx-datatable-column [resizeable]="false" [sortable]="true" prop="vin" name="VIN" [minWidth]="140">
          <ng-template let-row="row" ngx-datatable-cell-template>
            {{ row.vin }}
          </ng-template>
        </ngx-datatable-column>
        <ngx-datatable-column
          [resizeable]="false"
          [sortable]="true"
          prop="lastContact"
          name="Last Contact"
          [minWidth]="180"
        >
          <ng-template let-row="row" ngx-datatable-cell-template>
            {{ row.lastContact }}
          </ng-template>
        </ngx-datatable-column>
        <ngx-datatable-column [resizeable]="false" [sortable]="false" prop="configAge" name="Config Age" [minWidth]="120">
          <ng-template let-row="row" ngx-datatable-cell-template>
            {{ row.configAge }}
          </ng-template>
        </ngx-datatable-column>
        <ngx-datatable-column [resizeable]="false" [sortable]="true" prop="latestEvent" name="Latest Event" [minWidth]="160">
          <ng-template let-row="row" ngx-datatable-cell-template>
            {{ row.latestEvent }}
          </ng-template>
        </ngx-datatable-column>
        <ngx-datatable-column [resizeable]="false" [sortable]="false" prop="location" name="Location" [minWidth]="180">
          <ng-template let-row="row" ngx-datatable-cell-template>
            {{ row.location }}
          </ng-template>
        </ngx-datatable-column>
      </ngx-datatable>
    </div>
  </div>
</section>
